/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useContext, useMemo, useState } from 'react';
import { checkValue, localization, shuffleArrayIfNeeded } from 'src/components/common/helper';

// Module Context
import { useComponentContext } from 'src/components/common/modules/component-layout/component-context';

// Cpds
import { Cpds } from '@Cpds.Ui';
import CpdsGridSystem from 'src/components/common/cpds/cpds-grid-system/styles/index';

// Sub Components
import * as Shoppable from '../styles/v3-shoppable-one-up-card-carousel-style';
import { getTertiaryTextColor } from '../styles/v3-shoppable-one-up-card-carousel-style';
import Slider, { Settings } from 'react-slick';
import { css, ThemeContext } from 'styled-components';
import { CpdsAspectRatioType } from 'components/common/cpds/cpds-image/constants';
import { ThemeColorMode, themeToken } from 'components/common/cpds/utils/cpds-utils';
import {
  HotelDataFetch,
  HotelDataFetchProps,
} from 'components/common/modules/expedia-deals/hotel/hotel-data/hotel-data-fetch';
import { useCustomFilters } from 'components/common/modules/expedia-deals/hotel/hooks/useHotelDealHelper';
import { useStores } from 'utils/useStore';
import { aspectRatioReverseMap } from 'components/common/cpds/cpds-image';
import { ClickStreamEventConstants, ClickStreamEventType } from 'src/utils/event-wrapper';

export enum BackgroundVariants {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

const V3ShoppableOneUpCardCarousel: React.FC = () => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    componentProps: {
      data: { module, moduleColor, items, isRandom },
      key,
      id,
      ...rest
    },
  } = useComponentContext();

  const { context } = useStores();

  const themeContext: any = useContext(ThemeContext);
  const cpds = themeContext?.styles?.cpds;
  const { token } = themeToken(cpds, cpds?.themeColorMode, '');

  const hotelDataFetchProps: HotelDataFetchProps = {
    hotelIds: items.map((item) => item.hotel.hotelIds).join(','),
    checkInDate: items[0].hotel.checkInDate,
    checkOutDate: items[0].hotel.checkOutDate,
  };
  const { data: hotelList, loading } = HotelDataFetch(hotelDataFetchProps);
  let searchResults: any = {};
  // const isTestDomain = window?.location?.href?.indexOf('sandbox') > -1 || window?.location?.href?.indexOf('trunk') > -1;
  searchResults = !loading ? hotelList : {};

  const { filterPropertySearchListings, propertyStarRatings, searchCriteria } = useCustomFilters(
    searchResults,
    loading,
    items[0].hotel.isRandom,
  );

  const clickstreamData = (eventName,componentName, componentElementName, direction) => {
    const clickstreamData: ClickStreamEventConstants = {
      EVENT_TYPE: ClickStreamEventType.INTERACTION,
      EVENT_CATEGORY: 'shoppable_1_up_carousel',
      EVENT_NAME: eventName,
      USER_INTERFACES: {
        ...(componentName && { component_name: componentName }),
        ...(componentElementName && { component_element_name: componentElementName }),
        ...(direction && { scroll_direction: direction }), 
      },
    };
    return JSON.stringify(clickstreamData);
  };

  const cards = useMemo(() => {
    const filterListing = filterPropertySearchListings
      ?.map((data: any, index: number) => {
        const { id, propertyImage, propertyDetailsLink, name, price, star, neighborhood, reviews } = data;

        const relativeCard = items.find((i) => i.hotel.hotelIds.split(',').includes(id));

        if (!checkValue(relativeCard)) {
          return;
        }

        // price
        const priceValue =
          checkValue(price) && checkValue(price?.lead) && checkValue(price?.lead?.formatted)
            ? price?.lead?.formatted
            : undefined;

        // get dates
        const options = {
          skeleton: 'dddDMMM',
          locale: context?.locale || 'en',
        };

        const checkInFormatted = localization.formatDate(
          new Date(
            `${searchCriteria?.checkInDate?.year}/${searchCriteria?.checkInDate?.month}/${searchCriteria?.checkInDate?.day}`,
          ),
          options,
        );
        const checkOutFormatted = localization.formatDate(
          new Date(
            `${searchCriteria?.checkOutDate?.year}/${searchCriteria?.checkOutDate?.month}/${searchCriteria?.checkOutDate?.day}`,
          ),
          options,
        );
        let offerDate = `${checkInFormatted} - ${checkOutFormatted}`;
        offerDate = offerDate?.toLocaleLowerCase() === 'invalid date - invalid date' ? ' ' : offerDate;

        // get cta url
        const dealUrl = checkValue(propertyDetailsLink) ? propertyDetailsLink?.uri?.value : '';

        return {
          title: name,
          location: relativeCard.location,
          description: relativeCard.subhead,
          rating: reviews?.score,
          ratingComment: reviews?.superlative,
          numberOfReviews: reviews?.localizedTotal,
          price: priceValue,
          priceType: price?.roomNightMessage,
          date: offerDate,
          imageAspectRatio: aspectRatioReverseMap?.[relativeCard?.image?.aspectRatio],
          cta: {
            ctaText: relativeCard.cta.ctaText,
            ctaUrl: dealUrl,
            openUrlInNewTab: relativeCard.cta.openUrlInNewTab,
          },
          image: {
            asset: {
              url: propertyImage?.image?.url,
            },
          },
        };
      })
      .filter(Boolean);

    return shuffleArrayIfNeeded(filterListing, isRandom);
  }, [filterPropertySearchListings, items, searchCriteria, context?.locale]);

  const [currentCard, setCurrentCard] = useState(cards[0]);

  if (!checkValue(items, true) || !checkValue(cards, true)) {
    return;
  }

  const NextButton = (props) => {
    const { className, style, onClick } = props;
    return (
      <Cpds.Button
        buttonType="Primary"
        className={`cpds-carousel-controls ${className}`}
        onClick={onClick}
        tag="button"
        data-isclickstreamenabled={true} data-clickstreamdata= {clickstreamData('content_carousel_nav.selected','content_carousel_nav','content_carousel_arrow','right')}
      >
        <Cpds.Icon id="nextIcon" name="chevron-right" customSize={{ width: '4.94px', height: '8px' }}></Cpds.Icon>
      </Cpds.Button>
    );
  };

  const PrevButton = (props) => {
    const { className, style, onClick } = props;
    return (
      <Cpds.Button
        buttonType="Primary"
        className={`cpds-carousel-controls ${className}`}
        onClick={onClick}
        tag="button"
        data-isclickstreamenabled={true} data-clickstreamdata= {clickstreamData('content_carousel_nav.selected','content_carousel_nav','content_carousel_arrow','left')}
      >
        <Cpds.Icon id="prevIcon" name="chevron-left" customSize={{ width: '4.94px', height: '8px' }}></Cpds.Icon>
      </Cpds.Button>
    );
  };

  const settings: Settings = {
    arrows: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: false,
    dots: true,
    centerMode: false,
    centerPadding: '40px',

    appendDots: (dots:any) => {
      let currentSlideIndex = 0; 
      const enhancedDots = React.Children.map(dots, (dot, index) => {
        if (React.isValidElement(dot)) {
          const direction = index > currentSlideIndex ? 'right' : 'left';
          return React.cloneElement(dot as React.ReactElement, {
            'data-isclickstreamenabled': "true",
            'data-clickstreamdata': clickstreamData('content_carousel_nav.selected', 'content_carousel_nav','content_carousel_dots', direction),
            key: index,
          } as React.HTMLAttributes<HTMLLIElement>);
        }
        return dot;
      });

      return <Cpds.DottedPagination dots={enhancedDots} numDotsToShow={4} dotWidth={12} />;
    },
    beforeChange: (currentSlide: number, nextSlide: number) => {
      setCurrentCard(cards[nextSlide]);
    },
    nextArrow: <NextButton />,
    prevArrow: <PrevButton />,
    responsive: [
      {
        breakpoint: 1279,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          nextArrow: undefined,
          prevArrow: undefined,
        },
      },
    ],
  };

  const getColumnLayout = (aspectRatio: string) => {
    switch (aspectRatio) {
      case CpdsAspectRatioType.CPDS_4x3:
        return {
          image: {
            xl: { start: '1', span: '6' },
            md: { start: '1', span: '12' },
            sm: { start: '1', span: '6' },
          },
          content: {
            xl: { start: '8', span: '5' },
            md: { start: '1', span: '12' },
            sm: { start: '1', span: '6' },
          },
        };
      case CpdsAspectRatioType.CPDS_1x1:
        return {
          image: {
            xl: { start: '1', span: '6' },
            md: { start: '1', span: '12' },
            sm: { start: '1', span: '6' },
          },
          content: {
            xl: { start: '8', span: '5' },
            md: { start: '1', span: '12' },
            sm: { start: '1', span: '6' },
          },
        };
      case CpdsAspectRatioType.CPDS_4x5:
        return {
          image: {
            xl: { start: '1', span: '4' },
            md: { start: '1', span: '12' },
            sm: { start: '1', span: '6' },
          },
          content: {
            xl: { start: '6', span: '7' },
            md: { start: '1', span: '12' },
            sm: { start: '1', span: '6' },
          },
        };
      default:
        return {
          image: {
            xl: { start: '1', span: '6' },
            md: { start: '1', span: '12' },
            sm: { start: '1', span: '6' },
          },
          content: {
            xl: { start: '8', span: '5' },
            md: { start: '1', span: '12' },
            sm: { start: '1', span: '6' },
          },
        };
    }
  };

  return (
    <Cpds.Layout
      className="v3-container v3-shoppable-one-up-card-carousel"
      HtmlTag="section"
      id={checkValue(id) ? id : key?.next()}
    >
      <Shoppable.V3ShoppableOneUpCardCarousel
        aspectRatio={currentCard?.imageAspectRatio || cards[0]?.imageAspectRatio || CpdsAspectRatioType.CPDS_4x5}
        moduleColor={moduleColor}
      >
        <CpdsGridSystem.ContainerFluid
          display="flex"
          justifyContent="center"
          alignItems="center"
          padding={['0px', '0px', '0px', '0px']} // Padding for ContainerFluid
          className="shoppable-card"
        >
          <CpdsGridSystem.ContainerWrapper
            id="containerWrapper"
            padding_xl={['56px', '56px', '56px', '56px']}
            padding_lg={['56px', '56px', '56px', '56px']}
            padding_md={['32px', '16px', '0', '16px']}
            padding_sm={['32px', '16px', '0', '16px']}
            maxWidth="1440px !important"
            styleOverrides={{
              overflow: 'hidden',
              'background-color':
                moduleColor?.toLowerCase() === BackgroundVariants.TERTIARY
                  ? token?.module_v3_shoppable_one_carousel_background_tertiary_color || '#FFFFFF'
                  : moduleColor?.toLowerCase() === BackgroundVariants.SECONDARY
                  ? token?.module_v3_shoppable_one_carousel_background_secondary_color || '#FFFFFF'
                  : token?.module_v3_shoppable_one_carousel_background_primary_color || '#FFFFFF',
            }}
          >
            {checkValue(cards, true) && (
              <Slider {...settings}>
                {cards.map((card, index) => (
                  <div key={`shoppable-card-${index}`}>
                    <CpdsGridSystem.Container>
                      <CpdsGridSystem.Item
                        gridColumn={getColumnLayout(card.imageAspectRatio || CpdsAspectRatioType.CPDS_4x5).image}
                        className="shoppable-card_image_area"
                      >
                        <Shoppable.ImageContainer aspectRatio={card.imageAspectRatio || CpdsAspectRatioType.CPDS_4x5}>
                          <Cpds.Image
                            aspectRatio={card.imageAspectRatio || CpdsAspectRatioType.CPDS_4x5}
                            borderRadius={`CPDS_[${token?.module_v3_shoppable_one_carousel_image_border_radius || 0}]`}
                            src={card.image.asset.url}
                            alt={card.title}
                          />
                        </Shoppable.ImageContainer>
                      </CpdsGridSystem.Item>
                      <CpdsGridSystem.Item
                        gridColumn={getColumnLayout(card.imageAspectRatio || CpdsAspectRatioType.CPDS_4x5).content}
                        className="shoppable-card_detail_area"
                      >
                        {/*code here*/}
                        <Shoppable.Card
                          aspectRatio={card.imageAspectRatio || CpdsAspectRatioType.CPDS_4x5}
                          moduleColor={moduleColor}
                        >
                          <div>
                            <Shoppable.Title tag="h3" className="cpds-display-roman-book-03" moduleColor={moduleColor}>
                              {card.title}
                            </Shoppable.Title>
                            <Shoppable.LocationContainer>
                              <Cpds.Icon
                                name="location-filled"
                                customSize={{ width: '9.33px', height: '13.33px' }}
                                color={getTertiaryTextColor(
                                  moduleColor,
                                  token?.module_v3_shoppable_one_carousel_tertiary_text_color,
                                  token?.module_v3_shoppable_one_carousel_icon_color || '#191E3B',
                                )}
                                id={`location-icon-${index}`}
                                styleOverrides={css`
                                  padding: 1.33px 3.34px;
                                `}
                              />
                              <Shoppable.Location
                                tag="p"
                                className="cpds-meta-roman-medium-book-10"
                                moduleColor={moduleColor}
                              >
                                {card.location}
                              </Shoppable.Location>
                            </Shoppable.LocationContainer>
                            <Shoppable.Description
                              tag="p"
                              className="cpds-body-roman-book-08"
                              aspectRatio={card.imageAspectRatio || CpdsAspectRatioType.CPDS_4x5}
                              moduleColor={moduleColor}
                            >
                              {card.description}
                            </Shoppable.Description>
                          </div>
                          <Shoppable.RatingAndPrice>
                            {checkValue(card?.rating) && (
                              <Shoppable.RatingContainer moduleColor={moduleColor}>
                                {/*<Shoppable.Rating moduleColor={moduleColor}>
                                <Shoppable.RatingValue tag="p" className="cpds-meta-roman-medium-book-10" moduleColor={moduleColor}>
                                  {card.rating}
                                </Shoppable.RatingValue>
                              </Shoppable.Rating>
                              <Shoppable.RatingDescription>
                                <Shoppable.RatingLabel tag="p" className="cpds-meta-roman-medium-book-10" moduleColor={moduleColor}>
                                  {card.ratingComment}
                                </Shoppable.RatingLabel>
                                <Shoppable.Reviews tag="p" className="cpds-meta-roman-book-10" moduleColor={moduleColor}>
                                  {`${card.numberOfReviews} reviews`}
                                </Shoppable.Reviews>
                              </Shoppable.RatingDescription>*/}

                                <Cpds.Ratings
                                  rating={card?.rating}
                                  themeColorMode={
                                    moduleColor.toLowerCase() === BackgroundVariants.TERTIARY &&
                                    token?.module_v3_shoppable_one_carousel_tertiary_rating_background_color !==
                                      '#22823A'
                                      ? ThemeColorMode.Dark
                                      : ThemeColorMode.Light
                                  }
                                  ratingText={card?.ratingComment}
                                  numberOfReviews={card?.numberOfReviews}
                                />
                              </Shoppable.RatingContainer>
                            )}

                            <Shoppable.PriceInfo showBorder={checkValue(card?.rating)}>
                              {checkValue(card?.price) && (
                                <Shoppable.Price
                                  tag="p"
                                  className="cpds-heading-roman-med-06"
                                  moduleColor={moduleColor}
                                >
                                  {card.price}
                                </Shoppable.Price>
                              )}
                              {checkValue(card?.date) && (
                                <Shoppable.DateContainer>
                                  <Cpds.Icon
                                    name="calendar"
                                    customSize={{ width: '12.12px', height: '13.33px' }}
                                    color={getTertiaryTextColor(
                                      moduleColor,
                                      token?.module_v3_shoppable_one_carousel_tertiary_text_color,
                                      token?.module_v3_shoppable_one_carousel_icon_color || '#191E3B',
                                    )}
                                    id={`calendar-icon-${index}`}
                                  />
                                  {checkValue(card.priceType) && (
                                    <Shoppable.Date
                                      tag="p"
                                      className="cpds-meta-roman-book-10"
                                      moduleColor={moduleColor}
                                    >
                                      {`${card.date} <span>${card.priceType}</span>`}
                                    </Shoppable.Date>
                                  )}
                                </Shoppable.DateContainer>
                              )}
                            </Shoppable.PriceInfo>
                          </Shoppable.RatingAndPrice>

                          {checkValue(card.cta.ctaText) && (
                            <Cpds.Button
                              href={card.cta.ctaUrl}
                              target={card.cta.openUrlInNewTab ? '_blank' : '_self'}
                              className="cpds-meta-roman-medium-book-09"
                              buttonType="Primary"
                              tag="a"
                              buttonSize={'medium'}
                              styleOverrides={css`
                                width: 90px;
                                height: 40px;
                                padding: 12px;
                              `}
                              data-isclickstreamenabled={true} data-clickstreamdata= {clickstreamData('content_carousel_more.selected','content_carousel_more','','')}
                            >
                              {card.cta.ctaText}
                            </Cpds.Button>
                          )}
                          <Shoppable.Spacer style={{ width: '10px', height: '24px' }} />
                        </Shoppable.Card>
                      </CpdsGridSystem.Item>
                    </CpdsGridSystem.Container>
                  </div>
                ))}
              </Slider>
            )}
          </CpdsGridSystem.ContainerWrapper>
        </CpdsGridSystem.ContainerFluid>
      </Shoppable.V3ShoppableOneUpCardCarousel>
    </Cpds.Layout>
  );
};

export default V3ShoppableOneUpCardCarousel;
